import { backToNative, getNativeInfo, setNavigationBar, isIOS, setNavbarAsync, isAndroid, isInApp } from '@docs/common'
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { triggerLink, isVersionAfter, resolvePnFromSn } from 'common/util'

import styles from './index.module.scss'
import classes from 'classnames'
import getQuickStart, { IGuide, IGuideComponentType, IGuideBanner, IGuideLogo } from 'servers/getQuickStart'
import { Element } from 'react-scroll'
import { parse } from 'qs'
import { useAsyncEffect } from 'ahooks'
import { trackQuickPageClick, trackQuickPageDuration, trackQuickPageView } from 'common/track'
import useAraLayout from 'common/useAraLayout'

interface IFeatureProps {
  icon?: string
  title: string
  url: string
}

const Quickstart = () => {
  const [guide, setGuide] = useState<IGuide | null>(null)
  const scrollContainer = useRef<HTMLDivElement>(null)
  const [statusBarHeight, setStatusBarHeight] = useState(0)
  const [isApp, setIsApp] = useState(false)

  useAraLayout();

  const { title = '', subtitle = '', tabs = [], color = '' } = guide || {}

  //转换十六进制颜色为RGB颜色
  const useHexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 255, g: 255, b: 255 } // 默认白色
  }

  const rgb = useHexToRgb((tabs[0] as IGuideLogo)?.background ?? '')
  const gradient = `linear-gradient(180deg, rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.4) 0%, rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0) 100%)`

  const { pn, customPn, isDisableBack } = parse(window.location.search.substr(1)) as { pn: string, customPn: string, isDisableBack?: string | boolean }

  const id = useMemo(() => {
    return guide?.id || ''
  }, [guide?.id])

  // 如果链接有自定义customPn参数，使用customPn，优先级大于参数pn
  // 链接参数 pn 来源值是app传过来的device_sn，也可以是cms上面生成的link带的参数
  const newPn = customPn && customPn !== 'null' && customPn !== 'undefined' && customPn.trim() !== '' ? customPn : resolvePnFromSn(pn);

   // 判断是否显示返回按钮
   const showBackButton = useMemo(() => {
    // 如果isDisableBack为true（字符串或布尔值），则不显示返回按钮
    if (isDisableBack === 'true' || isDisableBack === true) {
      console.log('不显示返回按钮')
      return false
    }
    // 如果isDisableBack为null、undefined、false或未设置，则显示返回按钮
    console.log('显示返回按钮')
    return true
  }, [isDisableBack])


  // 通过pn获取数据
  useAsyncEffect(async () => {
    const guide = await getQuickStart({ pn: newPn, id })
    setGuide(guide)
  }, [])

  // 点击列表跳转
  const openLink = (feature: IFeatureProps) => {
    if (!feature.url) return
    trackQuickPageClick(feature.title, newPn)
    const link = feature.url
    triggerLink(link)
  }

  // 返回上一页
  const onBack = useCallback(() => {
    if ((window as any).__IS_APP__) {
      backToNative()
    } else {
      if (window.history.length > 1) {
        window.history.back()
      }
    }
  }, [])

  // app外禁用返回按钮
  useEffect(() => {
    const fun = async () => {
      const nativeInfo = await getNativeInfo().catch(e => null)
      if (!nativeInfo || !nativeInfo?.appVersion) {
        return
      }
      // const isApp = await isInApp()
      // setIsApp(isApp)
      setIsApp(!!nativeInfo?.token)


      const minSupportVersion = isIOS ? '4.7.80' : '4.7.8'
      const isImmersive = isVersionAfter(nativeInfo?.appVersion, minSupportVersion)
      setNavigationBar({
        hidden: true,
      })
      // 隐藏原生导航条

      const navbar = await setNavbarAsync({
        hideNavbar: true,
        backOperation: 1,
        immersiveMode: isImmersive,
      }).catch(err => {
        console.log(err)
      })

      if (isImmersive && navbar?.statusBarHeight) {
        // Android返回的是实际屏幕高度，需要除掉像素密度来获取实际视窗高度
        let viewpointHeight = navbar.statusBarHeight
        if (isAndroid) {
          viewpointHeight /= window.devicePixelRatio
        }
        setStatusBarHeight(viewpointHeight)
      }
    }
    fun()
  }, [])

  // 页面访问埋点
  useEffect(() => {
    id && trackQuickPageView(id, title, newPn)
  }, [id, title, newPn])

  useEffect(() => {
    const startTime = Date.now()
    const handleResize = () => {
      let duration = Math.floor((Date.now() - startTime) / 1000) // 计算停留时长，单位为毫秒
      // 调用埋点函数，传入停留时长
      id && trackQuickPageDuration(id, newPn, duration)
    }
    // 页面加载时开始计时
    if (isAndroid) {
      window.addEventListener('beforeunload', handleResize)
    }
    if (isIOS) {
      window.addEventListener('pagehide', handleResize)
      // 对于不支持 pagehide 的浏览器，使用 visibilitychange 事件
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          handleResize()
        }
      })
    }

    return () => {
      // 页面卸载时停止计时
      window.removeEventListener('beforeunload', handleResize)

      window.removeEventListener('pagehide', handleResize)
      document.removeEventListener('visibilitychange', handleResize)
    }
  }, [id, newPn])

  // 设置了字体颜色，使用字体颜色，否则使用默认颜色
  const styleTag = color ? `<style>.custom-color span { color: ${color}!important; }</style>` : ''
  console.log('isApp', isApp,showBackButton)
  return (
    <div className={styles.container} ref={scrollContainer} id="container">
      {tabs && tabs[0]?.__component === IGuideComponentType.Banner ? (
        <div
          className={styles.bannerBg}
          style={{
            backgroundImage: `url(${(tabs[0] as IGuideBanner)?.banner?.[0]?.url ?? ''})`,
            minHeight:
              ((tabs[0] as IGuideBanner)?.banner?.[0]?.height! / (tabs[0] as IGuideBanner)?.banner?.[0]?.width!) *
                375 || '209px',
          }}
        >
          {/* 背景图 */}
          {showBackButton && isApp &&(
            <i
              className={styles.back}
              onClick={onBack}
              style={{ top: statusBarHeight ? `${statusBarHeight + 20}px` : '40px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                <path
                  d="M14 7.41602L6 14.9993L14 22.5827"
                  stroke="#333333"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M6 15H24" stroke="#333333" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </i>
           )} 
          <div
            className={`${styles.title}  ${styles[color]} ${color ? 'custom-color' : ''}`}
            dangerouslySetInnerHTML={{ __html: styleTag + subtitle }}
            style={{
              color: color,
            }}
          />
        </div>
      ) : (
        <div className={styles.banner} style={{ backgroundImage: `url(${(tabs[0] as IGuideLogo)?.logo?.[0]?.url})` }}>
          {/* 主题色 */}
          <div className={styles.gradient} style={{ background: gradient }}></div>
          {showBackButton &&  isApp && (
            <i
              className={styles.back}
              onClick={onBack}
              style={{ top: statusBarHeight ? `${statusBarHeight + 20}px` : '40px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                <path
                  d="M14 7.41602L6 14.9993L14 22.5827"
                  stroke="#333333"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M6 15H24" stroke="#333333" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </i>
          )}
          <div
            className={`${styles.title}  ${color ? 'custom-color' : ''}`}
            dangerouslySetInnerHTML={{ __html: styleTag + subtitle }}
            style={{ color: color }}
          />
        </div>
      )}

      {tabs.map((tab, i) => (
        <Element name={`${i}`} id={`${i}`} key={`${tab?.id}`} className={styles.block}>
          <div
            className={styles.content}
            style={{
              margin: tab?.__component === IGuideComponentType.Banner ? ' -30px 10px 20px' : '-230px 10px 20px',
            }}
          >
            {tab?.listing?.map(feature => (
              <div
                className={classes(styles.cell, styles[tab?.layout ?? 'Vertical'])}
                onClick={() => openLink(feature)}
                key={feature?.id}
              >
                {feature.icon && (
                  <span className={styles.iconBackground}>
                    <i style={{ backgroundImage: `url(${feature.icon})` }}></i>
                  </span>
                )}
                <span className={styles.title}>{feature.title}</span>
                <i className={styles.arrow}></i>
              </div>
            ))}
          </div>
        </Element>
      ))}
    </div>
  )
}
export default Quickstart
